import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY_NAME, JURISDICTION, LEGAL_EMAIL, LAST_UPDATED } from "./legal";

const RED = "#C8000F";

const Section = ({ n, title, children }) => (
  <section className="mb-8" data-testid={`legal-section-${n}`}>
    <h2 className="font-display text-xl font-bold text-white mb-2">{n}. {title}</h2>
    <div className="text-sm text-slate-300 leading-relaxed space-y-3">{children}</div>
  </section>
);

function Shell({ title, children, testid }) {
  return (
    <div className="min-h-screen bg-[#0C1017] text-slate-100" style={{ fontFamily: "Inter, sans-serif" }} data-testid={testid}>
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-home-link" className="flex items-center">
            <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-10 w-auto object-contain" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{title}</h1>
        <p className="text-xs text-slate-500 mt-2 mb-8">Last updated: {LAST_UPDATED} · {COMPANY_NAME}</p>
        {children}
        <div className="mt-10 pt-6 border-t border-white/10 text-sm text-slate-400">
          Questions? <Link to="/contact" className="font-semibold" style={{ color: RED }}>Contact us</Link> or email{" "}
          <a href={`mailto:${LEGAL_EMAIL}`} className="font-semibold" style={{ color: RED }}>{LEGAL_EMAIL}</a>.
        </div>
      </main>
    </div>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy" testid="privacy-page">
      <p className="text-sm text-slate-300 leading-relaxed mb-8">
        This Privacy Policy explains how {COMPANY_NAME} ("we", "us", "our") collects, uses and protects
        your information when you use the Rodeo Pilot website and apps (the "Service"). By using the
        Service you agree to this policy.
      </p>
      <Section n="1" title="Information we collect">
        <p>We collect information you provide directly — your name, email address, competitor name,
        associations and events, the rodeos you save or enter, and any messages you send us. When you
        subscribe or buy credits, our payment processor (Stripe) handles your card details; we never
        store full card numbers.</p>
        <p>We also collect limited usage data (pages viewed, features used, device and approximate
        location) to operate and improve the Service.</p>
      </Section>
      <Section n="2" title="How we use it">
        <p>To provide and personalize the Service (schedules, draws, standings, tracking your riders),
        process subscriptions and credits, send you account and rodeo notifications, respond to your
        messages, prevent abuse, and improve our features.</p>
      </Section>
      <Section n="3" title="Sharing">
        <p>We do not sell your personal information. We share it only with service providers who help us
        run the Service (e.g. payment processing, email delivery, hosting), and when required by law or
        to protect our rights. Public rodeo results and standings come from official association sources.</p>
      </Section>
      <Section n="4" title="Data retention & security">
        <p>We keep your information for as long as your account is active or as needed to provide the
        Service and meet legal obligations. We use reasonable technical and organizational measures to
        protect it, though no method of transmission or storage is 100% secure.</p>
      </Section>
      <Section n="5" title="Your choices">
        <p>You can view and update your profile in the app, unsubscribe from non-essential emails, and
        request deletion of your account and personal data by contacting us. We will respond within a
        reasonable time as required by applicable law.</p>
      </Section>
      <Section n="6" title="Children">
        <p>The Service is not directed to children under 13, and we do not knowingly collect their
        personal information.</p>
      </Section>
      <Section n="7" title="Changes & governing law">
        <p>We may update this policy; material changes will be posted here with a new date. This policy
        is governed by the laws of the {JURISDICTION}.</p>
      </Section>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service" testid="terms-page">
      <p className="text-sm text-slate-300 leading-relaxed mb-8">
        These Terms of Service ("Terms") govern your use of the Rodeo Pilot website and apps (the
        "Service") provided by {COMPANY_NAME}. By creating an account or using the Service you agree to
        these Terms.
      </p>
      <Section n="1" title="The Service">
        <p>Rodeo Pilot helps rodeo athletes and fans find rodeos, plan their season, track entries,
        draws, standings and competitors. Rodeo data, schedules, draws and results are compiled from
        official association sources and are provided for convenience — we don't guarantee they are
        complete, current or error-free. Always confirm entry deadlines and official information with the
        sanctioning association.</p>
      </Section>
      <Section n="2" title="Accounts">
        <p>You're responsible for the accuracy of your account information and for activity under your
        account. Keep your sign-in secure and let us know of any unauthorized use.</p>
      </Section>
      <Section n="3" title="Subscriptions, credits & payments">
        <p>Paid plans and credit packs are billed through Stripe. Subscriptions renew automatically until
        cancelled; you can cancel anytime and keep access through the end of the paid period. Credits are
        a prepaid in-app currency used for certain actions, have no cash value, and are non-transferable.
        Except where required by law, payments and credits are non-refundable.</p>
      </Section>
      <Section n="4" title="Acceptable use">
        <p>Don't misuse the Service: no scraping or reselling our data, no attempts to disrupt or breach
        the Service, no unlawful, infringing or abusive content, and no impersonation. We may suspend or
        terminate accounts that violate these Terms.</p>
      </Section>
      <Section n="5" title="Intellectual property">
        <p>The Service, including its design, software and branding, belongs to {COMPANY_NAME}. You get a
        limited, personal, non-exclusive right to use it. Content you submit remains yours, but you grant
        us a licence to use it to operate the Service.</p>
      </Section>
      <Section n="6" title="Disclaimers & liability">
        <p>The Service is provided "as is" without warranties of any kind. To the fullest extent
        permitted by law, {COMPANY_NAME} is not liable for any indirect, incidental or consequential
        damages, or for decisions you make based on rodeo data in the Service.</p>
      </Section>
      <Section n="7" title="Changes & governing law">
        <p>We may update these Terms; continued use after changes means you accept them. These Terms are
        governed by the laws of the {JURISDICTION}, and you agree to the exclusive jurisdiction of its
        courts.</p>
      </Section>
    </Shell>
  );
}
